import Animated from '@/components/animatedComponents/Animated';
import { cn } from '@/lib/utils';
import { SubCategory } from '@/types/navbar/navbarTypes';
import { replaceSlash } from '@/utils/stringFunctions';
import Link from 'next/link';
import { Dispatch, SetStateAction } from 'react';

interface navCategoryProps {
  clickedCategory: string;
  categoryData: SubCategory[];
  onCloseDropdown: () => void;
  isReverse: boolean | null;
  selectedSubcategory: number;
  setSelectedSubcategory: Dispatch<SetStateAction<number>>;
}

const NavCategory: React.FC<navCategoryProps> = ({
  categoryData,
  clickedCategory,
  onCloseDropdown,
  isReverse,
  selectedSubcategory,
  setSelectedSubcategory,
}) => {
  if (!categoryData) return;

  const publishedCategories = categoryData?.filter(
    (c) => c?.status === 'published'
  );

  const selectedCategory = publishedCategories[selectedSubcategory];

  return (
    <>
      <div
        className={`flex flex-col w-64 py-6 px-4 overflow-y-scroll no-scrollbar md:max-h-[80vh] transition-transform transform shadow-right`}
      >
        {publishedCategories?.map((subCat, index) => {
          return (
            <Animated
              key={index}
              delay={index * 0.03}
              vertical={isReverse === null}
              reverse={isReverse as boolean}
              classList="w-56"
            >
              <div className={`flex flex-col w-56`}>
                <Link
                  href={`/products/${replaceSlash(clickedCategory)}/${
                    subCat?.slug
                  }`}
                  onClick={onCloseDropdown}
                  className="group cursor-pointer"
                  onMouseEnter={() => setSelectedSubcategory(index)}
                  prefetch
                >
                  <div
                    className={cn(
                      'text-sm font-proxima_font font-semibold text-black p-3.5 rounded-xl flex items-center justify-between hover:bg-gray',
                      index === selectedSubcategory && 'bg-gray'
                    )}
                  >
                    <span className="text-lg">
                      {subCat?.short_title ?? subCat?.title}
                    </span>

                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 256 256"
                      className={cn(
                        'opacity-0 -translate-x-2 group-hover:translate-x-0 group-hover:opacity-100 duration-200',
                        index === selectedSubcategory &&
                          'translate-x-0 opacity-100'
                      )}
                    >
                      <path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"></path>
                    </svg>
                  </div>
                </Link>
              </div>
            </Animated>
          );
        })}
      </div>
      {/* List of items in sub-category */}
      <div
        key={selectedSubcategory}
        className="px-4 py-6 grid grid-cols-3 flex-1 h-full"
        style={{ gridAutoRows: '1fr' }}
      >
        {selectedCategory?.sub_sub_categories
          ?.filter((subSubCat) => !subSubCat?.hidden)
          ?.filter((ssc) => ssc?.status === 'published')
          ?.map((subSubCat, index) => (
            <Animated
              key={index}
              delay={index * 0.03}
              reverse={isReverse as boolean}
              classList={cn('h-full')}
            >
              <Link
                href={`/products/${replaceSlash(clickedCategory)}/${
                  selectedCategory?.slug
                }/${subSubCat?.slug}`}
                className="flex h-full justify-between group p-3.5 rounded-xl cursor-pointer hover:bg-gray transition-all duration-200"
                onClick={onCloseDropdown}
                prefetch={true}
              >
                <div className="flex group flex-col gap-1">
                  <div className="text-sm font-proxima_font font-semibold text-black">
                    <span className="text-lg">
                      {subSubCat?.short_title ?? subSubCat?.title}
                    </span>
                  </div>
                  <div className="line-clamp-2 font-medium text-black/60 font-proxima_font">
                    {subSubCat?.description}
                  </div>
                </div>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 256 256"
                  className="w-5 flex-shrink-0 opacity-0 -translate-x-2 group-hover:translate-x-0 group-hover:opacity-100 duration-200"
                >
                  <path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"></path>
                </svg>
              </Link>
            </Animated>
          ))}
      </div>
    </>
  );
};

export default NavCategory;
